import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { MAIL_AUTH_LOST_EVENT, MAIL_AUTH_READY_EVENT } from "@/auth/constants";
import { isMailSessionVerified } from "@/auth/session";
import {
  warmCoffreSuiteConnection,
  type CoffreSuiteStatus,
} from "@/lib/suite/coffreSuiteApi";

const REFRESH_INTERVAL_MS = 90_000;

type CoffreSuiteStatusContextValue = {
  status: CoffreSuiteStatus | null;
  loading: boolean;
  refresh: () => Promise<CoffreSuiteStatus | null>;
};

const CoffreSuiteStatusContext = createContext<CoffreSuiteStatusContextValue | null>(null);

export function CoffreSuiteStatusProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<CoffreSuiteStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const refreshInFlightRef = useRef<Promise<CoffreSuiteStatus | null> | null>(null);

  const refresh = useCallback(async (): Promise<CoffreSuiteStatus | null> => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }
    const run = (async () => {
      setLoading(true);
      try {
        const next = await warmCoffreSuiteConnection();
        setStatus(next);
        return next;
      } finally {
        setLoading(false);
        refreshInFlightRef.current = null;
      }
    })();
    refreshInFlightRef.current = run;
    return run;
  }, []);

  useEffect(() => {
    const onReady = () => {
      void refresh();
    };
    const onLost = () => {
      setStatus(null);
      setLoading(false);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible" && isMailSessionVerified()) {
        void refresh();
      }
    };

    window.addEventListener(MAIL_AUTH_READY_EVENT, onReady);
    window.addEventListener(MAIL_AUTH_LOST_EVENT, onLost);
    document.addEventListener("visibilitychange", onVisibility);

    if (isMailSessionVerified()) {
      void refresh();
    }

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible" && isMailSessionVerified()) {
        void refresh();
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      window.removeEventListener(MAIL_AUTH_READY_EVENT, onReady);
      window.removeEventListener(MAIL_AUTH_LOST_EVENT, onLost);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(intervalId);
    };
  }, [refresh]);

  const value: CoffreSuiteStatusContextValue = {
    status,
    loading,
    refresh,
  };

  return (
    <CoffreSuiteStatusContext.Provider value={value}>{children}</CoffreSuiteStatusContext.Provider>
  );
}

export function useCoffreSuiteStatus(): CoffreSuiteStatusContextValue {
  const ctx = useContext(CoffreSuiteStatusContext);
  if (!ctx) {
    return {
      status: null,
      loading: false,
      refresh: async () => null,
    };
  }
  return ctx;
}
